import { useLocation, Link, Navigate } from "react-router-dom";
import Seo from "../components/Seo";
import Breadcrumbs, { breadcrumbJsonLd } from "../components/Breadcrumbs";
import { findService, SERVICES, ZONES } from "../data/services";
import { CITIES } from "../data/cities";
import { buildCityServicePath } from "./CityServicePage";
import { PHONE_DISPLAY, PHONE_TEL } from "../components/Layout";
import { siteUrl } from "../lib/siteUrl";
import PannesGrid from "../components/sections/PannesGrid";
import BrandsCloud from "../components/sections/BrandsCloud";
import FaqAccordion from "../components/sections/FaqAccordion";
import PageFinalCta from "../components/sections/PageFinalCta";
import RelatedPannes from "../components/sections/RelatedPannes";
import RelatedGeoPannes from "../components/sections/RelatedGeoPannes";
import ImageGallery from "../components/sections/ImageGallery";
import { APPLIANCE_IMAGES } from "../data/images";
import LastUpdated from "../components/LastUpdated";
import ReviewsStrip, { filterReviewsByService } from "../components/sections/ReviewsStrip";
import { BRANDS, brandPath } from "../data/brands";
import { LAST_UPDATED } from "../lib/lastUpdated";
import { getServiceVoiceFaq } from "../data/voice-faq";
import { VoiceFaqSection } from "../components/home/HomeSections";
import { findBrandApplianceFor } from "../data/brand-appliance";

const SLUG_TO_SERVICE_KEY = {
    "reparateur-lave-linge-paris": "lave-linge",
    "reparateur-lave-vaisselle-paris": "lave-vaisselle",
    "reparateur-frigo-paris": "frigo",
    "reparation-four-paris": "four",
    "reparation-seche-linge-paris": "seche-linge",
};

const HERO_BULLETS = [
    "Déplacement et diagnostic à domicile",
    "Devis ferme avant toute réparation",
    "Pièces d'origine ou premium équivalentes",
    "Garantie 18 mois pièces & main-d'œuvre",
    "Paiement après intervention",
    "Conseils d'entretien pour prolonger la durée de vie",
];

export default function ServicePage() {
    const location = useLocation();
    const slug = location.pathname.replace(/^\//, "");
    const s = findService(slug);
    if (!s) return <Navigate to="/" replace />;

    const crumbs = [
        { name: "Accueil", to: "/" },
        { name: "Services", to: "/" },
        { name: s.nav },
    ];

    const voiceFaq = getServiceVoiceFaq(s);

    const jsonLd = {
        "@context": "https://schema.org",
        "@graph": [
            breadcrumbJsonLd(crumbs),
            {
                "@type": "WebPage",
                "url": siteUrl(`/${s.slug}`),
                "name": s.h1,
                "inLanguage": "fr-FR",
                "dateModified": LAST_UPDATED,
                "speakable": { "@type": "SpeakableSpecification", "cssSelector": [".speakable-voice-faq"] },
            },
            {
                "@type": "Service",
                "name": s.h1,
                "serviceType": s.h1,
                "provider": { "@id": siteUrl("/#business") },
                "areaServed": ["Paris", "Hauts-de-Seine", "Yvelines", "Val-de-Marne"],
                "description": s.description,
                "offers": {
                    "@type": "Offer",
                    "priceCurrency": "EUR",
                    "price": "49",
                    "priceSpecification": {
                        "@type": "PriceSpecification",
                        "price": "49",
                        "priceCurrency": "EUR",
                        "description": "Déplacement et diagnostic à domicile, déduit du coût de la réparation",
                    },
                },
            },
            {
                "@type": "FAQPage",
                "mainEntity": [...s.faqs, ...voiceFaq].map((f) => ({
                    "@type": "Question",
                    "name": f.q,
                    "acceptedAnswer": { "@type": "Answer", "text": f.a },
                })),
            },
        ],
    };

    const otherServices = SERVICES.filter((x) => x.slug !== s.slug);
    const cityKey = SLUG_TO_SERVICE_KEY[s.slug];

    return (
        <>
            <Seo
                title={s.title}
                description={s.description}
                keywords={s.keywords}
                jsonLd={jsonLd}
                canonical={siteUrl(`/${s.slug}`)}
            />

            {/* HERO */}
            <section className="relative overflow-hidden grain">
                <div className="absolute -top-32 -right-20 w-[420px] h-[420px] bg-[var(--amber)]/20 rounded-full blur-3xl pointer-events-none" aria-hidden />
                <div className="max-w-7xl mx-auto px-5 lg:px-10 pt-10 pb-14 relative">
                    <Breadcrumbs items={crumbs} />
                    <div className="grid lg:grid-cols-12 gap-10 items-center mt-6">
                        <div className="lg:col-span-7">
                            <div className="flex items-center gap-3 mb-4">
                                <span className="w-12 h-12 rounded-2xl bg-[var(--navy)] text-[var(--amber)] grid place-items-center"><i className={`${s.icon} text-xl`} /></span>
                                <span className="text-xs uppercase tracking-[0.18em] font-semibold text-[var(--ink-soft)]">Service spécialisé</span>
                            </div>
                            <h1 className="heading text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05]">{s.h1}</h1>
                            <p className="mt-5 text-lg text-[var(--ink-soft)] max-w-2xl">{s.intro}</p>
                            <div className="mt-4"><LastUpdated /></div>
                            <div className="mt-7 flex flex-wrap gap-3">
                                <a href={PHONE_TEL} className="btn-amber inline-flex items-center gap-2 px-5 py-3.5 rounded-full" data-testid={`service-phone-${s.slug}`}>
                                    <i className="fa-solid fa-phone-volume" /> Appeler {PHONE_DISPLAY}
                                </a>
                                <Link to="/contact" className="btn-outline-navy inline-flex items-center gap-2 px-5 py-3.5 rounded-full font-semibold">
                                    <i className="fa-regular fa-pen-to-square" /> Devis gratuit
                                </Link>
                            </div>
                        </div>

                        <aside className="lg:col-span-5">
                            <div className="card-soft p-6">
                                <h2 className="heading text-lg font-bold flex items-center gap-2"><i className="fa-solid fa-list-check text-[var(--amber)]" /> Inclus dans l&apos;intervention</h2>
                                <ul className="mt-4 space-y-2.5 text-sm">
                                    {HERO_BULLETS.map((t) => (
                                        <li key={t} className="flex items-start gap-2"><i className="fa-solid fa-check text-[var(--amber)] mt-1" /> <span>{t}</span></li>
                                    ))}
                                </ul>
                            </div>
                        </aside>
                    </div>
                </div>
            </section>

            <PannesGrid
                pannes={s.pannes}
                title="Pannes les plus fréquentes que nous réparons"
                intro="Diagnostic précis, intervention ciblée, durabilité garantie."
            />

            {cityKey && <RelatedPannes applianceKey={cityKey} />}

            <RelatedGeoPannes applianceServiceSlug={s.slug} />

            <BrandsCloud
                brands={s.brands}
                title={`Toutes marques de ${s.nav.toLowerCase()} réparées`}
                intro="Nos techniciens sont formés sur l'ensemble du marché européen."
                bgClassName="bg-[var(--paper)] border-y border-[var(--line)]"
            />

            {/* PAGES MARQUE */}
            <section className="max-w-7xl mx-auto px-5 lg:px-10 py-10" data-testid="service-brand-links">
                <p className="text-xs uppercase tracking-[0.18em] font-semibold text-[var(--amber)] mb-3">Réparation par marque</p>
                <div className="flex flex-wrap gap-2.5">
                    {BRANDS.map((b) => {
                        const combo = findBrandApplianceFor(b.name, s.slug);
                        const linkTo = combo ? `/${combo.slug}` : brandPath(b);
                        return (
                            <Link
                                key={b.slug}
                                to={linkTo}
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[var(--line)] hover:border-[var(--amber)] hover:bg-[var(--paper)] text-sm font-medium text-[var(--navy)] transition"
                                data-testid={`service-brand-link-${b.slug}`}
                            >
                                <i className="fa-solid fa-industry text-[var(--amber)] text-xs" />
                                Réparation {b.name} Paris
                            </Link>
                        );
                    })}
                </div>
            </section>

            {/* ZONES */}
            <section className="max-w-7xl mx-auto px-5 lg:px-10 py-16">
                <div className="divider-amber mb-5" />
                <h2 className="heading text-2xl lg:text-3xl font-extrabold">Où intervenons-nous pour le {s.nav.toLowerCase()} ?</h2>
                <p className="mt-3 text-[var(--ink-soft)]">Service local en Île-de-France, technicien à proximité.</p>

                {cityKey && (
                    <div className="mt-6">
                        <p className="text-xs uppercase tracking-[0.18em] font-semibold text-[var(--amber)] mb-3">Pages dédiées par ville</p>
                        <div className="flex flex-wrap gap-2.5">
                            {CITIES.map((c) => (
                                <Link
                                    key={c.slug}
                                    to={buildCityServicePath(cityKey, c.slug)}
                                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[var(--line)] hover:border-[var(--amber)] hover:bg-[var(--paper)] text-sm font-medium text-[var(--navy)] transition"
                                    data-testid={`service-city-link-${c.slug}`}
                                >
                                    <i className="fa-solid fa-location-dot text-[var(--amber)] text-xs" />
                                    {s.nav} à {c.name}
                                </Link>
                            ))}
                        </div>
                    </div>
                )}

                <div className="grid md:grid-cols-2 gap-5 mt-8">
                    {Object.entries(ZONES).map(([dept, cities]) => (
                        <div key={dept} className="card-soft p-6">
                            <h3 className="heading text-lg font-bold flex items-center gap-2"><i className="fa-solid fa-location-dot text-[var(--amber)]" />{dept}</h3>
                            <p className="mt-3 text-sm text-[var(--ink-soft)]">{cities.join(" · ")}</p>
                        </div>
                    ))}
                </div>
                <Link to="/zones-intervention" className="mt-6 inline-flex items-center gap-2 text-[var(--navy)] font-semibold link-underline">Voir le détail des zones <i className="fa-solid fa-arrow-right" /></Link>
            </section>

            <ReviewsStrip reviews={filterReviewsByService(s.nav)} title={`Avis clients — ${s.nav}`} />

            <VoiceFaqSection items={voiceFaq} />

            <FaqAccordion title={`FAQ – ${s.nav}`} faqs={s.faqs} />

            <ImageGallery
                title={`${s.nav} en images`}
                subtitle={`Nos interventions au quotidien sur le ${s.nav.toLowerCase()} dans Paris et l'Île-de-France.`}
                images={APPLIANCE_IMAGES[SLUG_TO_SERVICE_KEY[s.slug]] || []}
            />

            {/* RELATED */}
            <section className="max-w-7xl mx-auto px-5 lg:px-10 py-16">
                <div className="divider-amber mb-5" />
                <h2 className="heading text-2xl lg:text-3xl font-extrabold">Autres services de dépannage électroménager</h2>
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mt-8">
                    {otherServices.map((x) => (
                        <Link key={x.slug} to={`/${x.slug}`} className="card-soft p-5 block">
                            <i className={`${x.icon} text-[var(--amber)] text-xl`} />
                            <h3 className="heading text-base font-bold mt-3">{x.nav}</h3>
                            <p className="text-xs text-[var(--ink-soft)] mt-1">Réparation à domicile · toutes marques</p>
                        </Link>
                    ))}
                </div>
            </section>

            <PageFinalCta
                title={`Une panne de ${s.nav.toLowerCase()} ? On est là.`}
                subtitle="Diagnostic à domicile, devis transparent, garantie 18 mois."
            />
        </>
    );
}
